import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { ApiKey } from '../../../../../../../../../typings/ApiKey.interface';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Divider from '../../../../../common/base/Divider';
import Icon from '../../../../../common/base/Icon';
import Text from '../../../../../common/base/Text';
import CopyableField from '../../../../../common/CopyableField';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { ApiKeyActionsMenu } from '../../../../../common/ApiKeys/ApiKeyActionsMenu';
import { ApiKeyLabelCell, ApiKeyPermissionsBadge } from '../../../../../common/ApiKeys/ApiKeyLabelCell';
import { UnauthorizedApiKeyRow } from '../../../../../common/ApiKeys/UnauthorizedApiKeyRow';
interface OrganizationApiKeyProps {
  api_keys: ApiKey[];
  // Full, unfiltered count of org keys. When provided, the body height is fixed to this count so
  // searching doesn't make the table jump around.
  total?: number;
  // The viewer can't list API keys (the server returns 401/403 to non-admins). Render the same
  // table shell with a single masked, disabled placeholder row instead of real data.
  unauthorized?: boolean;
  onEditKey: (api_key: ApiKey) => void;
  onRollKey: (api_key: ApiKey) => void;
  onDeleteKey: (api_key: ApiKey) => void;
}
const PAGE_SIZE = 10;
const ROW_HEIGHT = 45;

// Aligned with the Project API keys table stacked below: the Name track spans that table's
// Name + Project tracks (86px + 12px gap + 86px), so the Permissions and Key columns of
// both tables line up. Tracks + 12px gaps reproduce the design's 104px-per-column rhythm.
const grid_values = {
  columns: 'minmax(0, 184px) minmax(0, 80px) 1fr',
  gap: 3
};
interface OrganizationApiKeyRowProps {
  api_key: ApiKey;
  onEditKey: (api_key: ApiKey) => void;
  onRollKey: (api_key: ApiKey) => void;
  onDeleteKey: (api_key: ApiKey) => void;
}
function OrganizationApiKeyRow(t0) {
  const $ = _c(28);
  const {
    api_key,
    onEditKey,
    onRollKey,
    onDeleteKey
  } = t0;
  const [is_secret, setIsSecret] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== api_key) {
    t2 = <Div min_w={0}><ApiKeyLabelCell api_key={api_key} ellipsis={true} hide_permissions={true} /></Div>;
    $[1] = api_key;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== api_key) {
    t4 = <Div min_w={0} flex={t3}><ApiKeyPermissionsBadge api_key={api_key} /></Div>;
    $[4] = api_key;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      y: 2
    };
    t6 = {
      align: "center",
      gap: 1
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      grow: true
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== api_key.key || $[10] !== is_secret) {
    t8 = <CopyableField mono={true} ghost={true} text_size="xs" flex={t7} value={api_key.key} secret={is_secret} reveal_on_hover={false} visible_values={10} />;
    $[9] = api_key.key;
    $[10] = is_secret;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = is_secret ? "view" : "dismiss";
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setIsSecret(_temp);
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = <Button icon={t9} ghost={true} onClick={t10} />;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== api_key || $[16] !== onDeleteKey || $[17] !== onEditKey || $[18] !== onRollKey) {
    t12 = <ApiKeyActionsMenu api_key={api_key} onEditKey={onEditKey} onRollKey={onRollKey} onDeleteKey={onDeleteKey} ghost={true} />;
    $[15] = api_key;
    $[16] = onDeleteKey;
    $[17] = onEditKey;
    $[18] = onRollKey;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t8) {
    t13 = <Div min_w={0}><Div p={t5} flex={t6}>{t8}{t11}{t12}</Div></Div>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t2 || $[26] !== t4) {
    t14 = <StyledCardSection p={t1} grid={grid_values}>{t2}{t4}{t13}</StyledCardSection>;
    $[24] = t13;
    $[25] = t2;
    $[26] = t4;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}
function _temp(prev) {
  return !prev;
}
export function OrganizationApiKeysTable(t0) {
  const $ = _c(32);
  const {
    api_keys,
    total,
    unauthorized,
    onEditKey,
    onRollKey,
    onDeleteKey
  } = t0;
  const [page, setPage] = useState(1);
  let t1;
  if ($[0] !== api_keys) {
    t1 = api_keys.filter(_temp2);
    $[0] = api_keys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const organization_api_keys = t1;
  const total_count = total ?? organization_api_keys.length;
  const has_pagination = total_count > PAGE_SIZE;
  const body_height = total !== undefined ? Math.min(total_count, PAGE_SIZE) * ROW_HEIGHT : has_pagination ? PAGE_SIZE * ROW_HEIGHT : undefined;
  let t2;
  if ($[2] !== body_height) {
    t2 = body_height !== undefined ? {
      px: body_height
    } : undefined;
    $[2] = body_height;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const body_h = t2;
  const page_count = Math.max(1, Math.ceil(organization_api_keys.length / PAGE_SIZE));
  const current_page = Math.min(page, page_count);
  let t3;
  if ($[4] !== current_page || $[5] !== has_pagination || $[6] !== organization_api_keys) {
    t3 = has_pagination ? organization_api_keys.slice((current_page - 1) * PAGE_SIZE, current_page * PAGE_SIZE) : organization_api_keys;
    $[4] = current_page;
    $[5] = has_pagination;
    $[6] = organization_api_keys;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const paginated_api_keys = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      x: 4
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      grow: false
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Div flex={t5}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Name</Text></Div>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      grow: false
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Div flex={t7}><Text muted={true} semi={true} size="xs" p={{
        y: 1
      }}>Permissions</Text></Div>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      grow: true,
      justify: "space-between"
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <StyledCardSection muted={true} p={t4}><Div grid={grid_values}>{t6}{t8}<Div flex={t9}><Text muted={true} semi={true} size="xs" p={{
            y: 1
          }}>Key</Text></Div></Div></StyledCardSection>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== body_h || $[16] !== onDeleteKey || $[17] !== onEditKey || $[18] !== onRollKey || $[19] !== paginated_api_keys || $[20] !== total_count || $[21] !== unauthorized) {
    t11 = unauthorized ? <UnauthorizedApiKeyRow grid_values={grid_values} data-testid="organization-api-keys-unauthorized-row" /> : total_count === 0 ? <StyledCardSection p={3}><Text muted={true}><Icon icon="info" muted={true} left={true} />No organization API keys yet.</Text></StyledCardSection> : paginated_api_keys.length === 0 ? <StyledCardSection h={body_h} flex={{
      align: "center",
      justify: "center"
    }}><Text muted={true}><Icon icon="info" muted={true} left={true} />No matching API keys.</Text></StyledCardSection> : <StyledCardSection h={body_h}>{paginated_api_keys.map(api_key_0 => <OrganizationApiKeyRow key={api_key_0.id} api_key={api_key_0} onEditKey={onEditKey} onRollKey={onRollKey} onDeleteKey={onDeleteKey} />)}{paginated_api_keys.length < Math.min(total_count, PAGE_SIZE) && <Divider />}</StyledCardSection>;
    $[15] = body_h;
    $[16] = onDeleteKey;
    $[17] = onEditKey;
    $[18] = onRollKey;
    $[19] = paginated_api_keys;
    $[20] = total_count;
    $[21] = unauthorized;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== current_page || $[24] !== has_pagination || $[25] !== organization_api_keys.length || $[26] !== page_count || $[27] !== paginated_api_keys) {
    t12 = has_pagination && <StyledCardSection><Div p={{
        x: 4,
        y: 3
      }} flex={{
        justify: "space-between",
        align: "center"
      }}><Text muted={true} size="s">Viewing {paginated_api_keys.length} of {organization_api_keys.length}</Text><Div flex={{
          align: "center",
          gap: 2
        }}><Button onClick={() => setPage(Math.max(1, current_page - 1))} outline={true} icon="chevron_left" disabled={current_page <= 1} /><Button onClick={() => setPage(Math.min(page_count, current_page + 1))} outline={true} icon="chevron_right" disabled={current_page >= page_count} /></Div></Div></StyledCardSection>;
    $[23] = current_page;
    $[24] = has_pagination;
    $[25] = organization_api_keys.length;
    $[26] = page_count;
    $[27] = paginated_api_keys;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <StyledCard overflow_hidden={true}>{t10}{t11}{t12}</StyledCard>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function _temp2(api_key) {
  return !api_key.team_id;
}